import { Component, ViewChild } from '@angular/core';
import {
  IonContent,
  ViewDidEnter,
  ViewDidLeave,
  ViewWillEnter,
} from '@ionic/angular';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { Observable } from 'rxjs';
import { SortableSectionHeader } from '../shared/components/sortable-section/models/sortable-section.model';
import { NavigationHelperService } from '../shared/helpers/navigation.helpers.service';
import {
  CostComparisonModalOutputUI,
  InventoryCardEllipsisClick,
  InventoryPageData,
} from './models/inventory-view.model';
import { InventoryService } from './services/inventory.service';
import { ExtendedCostReportEvent } from './models/inventory-extended-cost-report.model';
import {
  CustomerMigration,
  MigrationRequest,
  MigrationStatus,
} from '@usf/inventory-types';
import { Customer } from '@usf/customer-types';
import { InventoryMigrationViewModel } from './models/inventory-migration-view-model.model';
import { TranslateService } from '@ngx-translate/core';
import { CostComparisonReportContainerComponent } from './components/cost-comparison-report-container/cost-comparison-report-container.component';
import { RadioBtnInventory } from './models/radio-btn-inventory.model';
import { CostComparisonReportModalOutput } from '@usf/ngrx-inventory';

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.page.html',
  styleUrls: ['./inventory.page.scss'],
})
export class InventoryPage
  implements ViewWillEnter, ViewDidEnter, ViewDidLeave
{
  // child components
  @ViewChild(IonContent) content: IonContent;
  @ViewChild(CostComparisonReportContainerComponent)
  costComparisonModal: CostComparisonReportContainerComponent;
  // static UI
  overlayGradient =
    'linear-gradient(90deg, rgba(47,47,47,0.77) 0%, rgba(47,47,47,0.77) 8%, rgba(47,47,47,0) 50%, rgba(47,47,47,0.77) 91%, rgba(47,47,47,0.77) 100%)';
  tileGradient = 'linear-gradient(to bottom, #000 0%, rgba(0, 0, 0, 0) 100%)';
  backgroundImageStyle = "url('/assets/images/inventory-hero.png')";
  isTouch: boolean = this.platformService.isTouch.value;
  // observables
  inventoryLandingPageData$: Observable<InventoryPageData>;
  ellipsisFlag$: Observable<boolean>;
  migrationFlag$: Observable<boolean>;
  migrationData$: Observable<InventoryMigrationViewModel>;
  selectedCustomer$: Observable<Customer>;
  MigrationStatus = MigrationStatus;
  inventoriesForCostComparison$: Observable<RadioBtnInventory[]>;

  constructor(
    readonly inventoryService: InventoryService,
    readonly platformService: PlatformService,
    private panAppState: PanAppState,
    private navigationHelperService: NavigationHelperService,
    private translateService: TranslateService,
  ) {}
  ionViewWillEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.inventory),
    );
    this.inventoryLandingPageData$ =
      this.inventoryService.recentInventoriesView$(
        this.platformService.platformType,
      );
    this.ellipsisFlag$ = this.panAppState.feature$([
      FEATURES.split_global_new_inventory_feature.name,
    ]);
    this.migrationFlag$ = this.inventoryService.selectInventoryMigrationFlag$();
    this.migrationData$ =
      this.inventoryService.selectCustomerMigrationStatus$();
    this.selectedCustomer$ = this.inventoryService.selectedCustomer$();
    this.inventoryService.trackInventoryPageLoaded();
    this.inventoriesForCostComparison$ =
      this.inventoryService.selectCompletedRadioBtnInventories$();
  }

  ionViewDidEnter(): void {
    window.dispatchEvent(new Event('resize'));
    this.content?.scrollToTop();
  }

  goToInventoryWorksheet(path: string) {
    this.navigationHelperService.routeTo(path);
  }

  goToR2PastInventories() {
    this.inventoryService.goToR2Inventory();
  }

  updateSort(sortableSectionHeader: SortableSectionHeader) {
    this.inventoryService.updateRecentInventoriesSort(
      sortableSectionHeader?.headerName,
      sortableSectionHeader?.sortDirection,
    );
  }

  openInventoryCardEllipsis(
    data: InventoryCardEllipsisClick,
    inventoryPageData: InventoryPageData,
  ) {
    this.inventoryService.openInventoryCardEllipsis(data, inventoryPageData);
  }

  goToAllInventories() {
    this.inventoryService.goTo([PATHS.INVENTORY, PATHS.ALL_INVENTORY]);
  }

  goToCreateInventory() {
    this.inventoryService.goTo([PATHS.INVENTORY, PATHS.CREATE_INVENTORY]);
  }

  copyInventory(inventoryId: string) {
    this.inventoryService.setInventoryToCopy(inventoryId);
    this.navigationHelperService.routeTo(
      PATHS.INVENTORY.concat('/', PATHS.CREATE_INVENTORY),
    );
  }

  openOtherInventoryActions(
    event: Event,
    inventoryPageData: InventoryPageData,
  ) {
    const ellipsisOptions =
      this.inventoryService.constructOtherInventoryActionOptions(
        this.isTouch,
        inventoryPageData,
        this.costComparisonModal,
      );
    this.inventoryService.openOtherInventoryActions(
      ellipsisOptions,
      this.isTouch,
      event,
    );
  }

  handleHideExtendedCostReport() {
    this.inventoryService.showHideExtendedCostReport(false);
  }

  handleSubmitExtendedCostReport(event: ExtendedCostReportEvent) {
    this.inventoryService.showHideExtendedCostReport(false);
    this.inventoryService.downloadExtendedCostReport(event.dateRange);
  }

  openMigrationConfirmationModal(
    customerName: string,
    customerNumber: number,
    departments: string[],
  ) {
    const departmentNumbers = departments.map(value => Number(value));
    if (!departmentNumbers.find(dep => dep === 0)) {
      departmentNumbers.unshift(0);
    }
    const departmentList: MigrationRequest = {
      departmentsToMigrate: departmentNumbers,
    };
    this.inventoryService.openMigrationConfirmationModal(
      customerName,
      customerNumber,
      departmentList,
    );
  }

  handleCostComparisonSubmission(event: CostComparisonModalOutputUI) {
    this.inventoryService.downloadCostComparisonReport(event);
  }

  ionViewDidLeave(): void {
    this.inventoryService.resetRecentInventoriesSort();
  }
}
